<template>
  <div class="view-all-panel">
    <div class="panel-header">
      <button
        class="close-btn"
        aria-label="Close panel"
        @click="$emit('close')"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h2 class="panel-title">
        Support chat conversations
      </h2>
    </div>
    <p class="panel-subtitle">
      Support chats are group conversations between users and admins at an organization. These are the support chats you are a participant in.
      <a
        href="https://support.optixapp.com/en/articles/13260740-what-is-inbox#h_5b2fc50180"
        target="_blank"
        rel="noopener noreferrer"
        class="learn-more-link"
      >Learn more</a>
    </p>
    <div class="panel-body">
      <div
        v-if="loading"
        class="panel-loading"
      >
        Loading...
      </div>
      <div
        v-else-if="error"
        class="panel-error"
      >
        Error: {{ error.message }}
      </div>
      <template v-else-if="conversations.length > 0">
        <div class="conversations-list">
          <div
            v-for="(conversation, index) in visibleConversations"
            :key="conversation.conversation_id || index"
            class="conversation-item"
            @click="handleConversationClickGuarded(conversation)"
          >
            <div class="conversation-image">
              <img
                v-if="conversation.avatar"
                :src="conversation.avatar"
                :alt="conversation.name"
              >
              <div
                v-else
                class="image-placeholder"
              >
                {{ getInitials(conversation.name) }}
              </div>
            </div>
            <div class="conversation-info">
              <span class="conversation-name">{{ conversation.name }}</span>
              <span class="conversation-meta">{{ getMessageSnippet(conversation) }}</span>
            </div>
          </div>
        </div>
        <button
          v-if="hasMore"
          class="load-more-btn"
          @click="loadMore"
        >
          Load more
        </button>
      </template>
      <div
        v-else
        class="panel-empty"
      >
        No conversations found
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onUnmounted } from 'vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { pushConversationPanel } from '../composables/useOptixPanel.js'
import { getOrganizationIdFromURL } from '../utils/tokenHandler.js'

defineEmits(['close'])

const organizationId = computed(() => getOrganizationIdFromURL())

const supportChatQuery = `
  query SupportChatConversations(
    $organizationId: ID
    $conversationType: [ConversationType!]
  ) {
    conversations(
      organization_id: $organizationId
      conversation_type: $conversationType
    ) {
      total
      data {
        conversation_id
        name
        message_count
        latest_message {
          user_id
          message
          plaintext
          timestamp
        }
        image {
          url
        }
      }
    }
  }
`

const { data, loading, error, cleanup } = useGraphQLData({
  dataKey: 'support_chat_conversations_view_all',
  query: supportChatQuery,
  operationName: 'SupportChatConversations',
  variables: computed(() => ({
    organizationId: organizationId.value || undefined,
    conversationType: ['USER_TO_ADMINS']
  })),
  priority: 1
})

const conversations = computed(() => {
  const items = data.value?.conversations?.data ?? []
  return [...items]
    .filter((item) => item.name && item.name.includes('&'))
    .sort((a, b) => (b.message_count ?? 0) - (a.message_count ?? 0))
    .map((item) => ({
      conversation_id: item.conversation_id,
      name: item.name || 'Unnamed Conversation',
      avatar: getAvatarUrl(item.image?.url, item.name || item.conversation_id),
      messageCount: item.message_count || 0,
      latest_message: item.latest_message || null
    }))
})

const PAGE_SIZE = 20
const visibleCount = ref(PAGE_SIZE)
const visibleConversations = computed(() => conversations.value.slice(0, visibleCount.value))
const hasMore = computed(() => visibleCount.value < conversations.value.length)
const loadMore = () => { visibleCount.value += PAGE_SIZE }

const getMessageSnippet = (conv) => {
  const msg = conv.latest_message
  if (!msg) return 'No messages yet'
  return (msg.plaintext || msg.message || '').trim() || 'No messages yet'
}

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  return words.length >= 2 ? (words[0][0] + words[words.length - 1][0]).toUpperCase() : name.charAt(0).toUpperCase()
}

const { guard } = useTrialModeGuard()
const handleConversationClickGuarded = guard((conversation) => {
  if (!conversation?.conversation_id) return
  pushConversationPanel(conversation.conversation_id)
})

onUnmounted(() => { cleanup() })
</script>

<style scoped>
.view-all-panel {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  flex-shrink: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
}

.panel-title {
  flex: 1;
  font-size: 24px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.panel-subtitle {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.5;
  margin: 0;
  padding: 0 24px 16px;
}

.learn-more-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
}

.learn-more-link:hover {
  text-decoration: underline;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 24px;
}

.panel-loading,
.panel-empty {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.panel-error {
  font-size: 14px;
  color: var(--color-red);
}

.conversations-list {
  display: flex;
  flex-direction: column;
}

.conversation-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  transition: background-color 0.2s;
}

.conversation-item:last-child {
  border-bottom: none;
}

.conversation-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.conversation-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.conversation-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 16px;
}

.conversation-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.conversation-name {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-meta {
  display: block;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.load-more-btn {
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: white;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.load-more-btn:hover {
  background: #f3f4f6;
}
</style>
